import React, { useEffect, useRef, useState } from "react";

export default function ConsultationModal({ type, onClose, interests = [] }) {
  const audit = type === "audit";
  const ai = type === "ai";
  const title = audit ? "UX audit consultation" : ai ? "AI training & integration consultation" : "Project consultation";
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const dialog = useRef(null);
  const calendar =
    (audit
      ? process.env.REACT_APP_UX_AUDIT_BOOKING_URL
      : process.env.REACT_APP_PROJECT_BOOKING_URL) ||
    process.env.REACT_APP_GOOGLE_CALENDAR_BOOKING_URL;
  const safeCalendar =
    calendar && /^https:\/\//.test(calendar) ? calendar : null;
  useEffect(() => {
    const previous = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialog.current.focus();
    return () => {
      document.body.style.overflow = overflow;
      previous?.focus();
    };
  }, []);
  const keys = (e) => {
    if (e.key === "Escape" && !busy) onClose();
    if (e.key !== "Tab") return;
    const items = [
      ...dialog.current.querySelectorAll(
        "button:not(:disabled), a[href], input, textarea, select",
      ),
    ];
    const first = items[0],
      last = items[items.length - 1];
    if (
      e.shiftKey &&
      (document.activeElement === first ||
        document.activeElement === dialog.current)
    ) {
      e.preventDefault();
      last?.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first?.focus();
    }
  };
  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    const data = Object.fromEntries(new FormData(e.currentTarget));
    if (
      !data.first_name.trim() ||
      !data.last_name.trim() ||
      !data.description.trim()
    ) {
      setError("Please enter your name and a little about your project.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || "http://localhost:8000"}/api/clients/book-call`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            first_name: data.first_name.trim(),
            last_name: data.last_name.trim(),
            email: data.email,
            phone: data.phone || "",
            role_type: data.role_type,
            project_description: `[${title}]\nInterests: ${interests.length ? interests.join("; ") : "To discuss"}\n${audit ? "Product URL" : "Project stage"}: ${data.context || "Not provided"}\n${data.description.trim()}`,
            timeline: data.timeline || "To discuss",
            start_date: "",
            budget: Number(data.budget) || 0,
            urgency: 5,
            use_ai_summarization: false,
          }),
        },
      );
      if (!response.ok) {
        const result = await response.json().catch(() => ({}));
        throw new Error(
          typeof result.detail === "string"
            ? result.detail
            : "We couldn’t save your inquiry. Please try again.",
        );
      }
      setSaved(true);
    } catch (err) {
      setError(err.message || "We couldn’t connect. Please try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="studio-modal-backdrop">
      <section
        className="studio-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="consultation-title"
        tabIndex={-1}
        ref={dialog}
        onKeyDown={keys}
      >
        <button
          className="studio-modal-close"
          onClick={onClose}
          disabled={busy}
          aria-label="Close consultation form"
        >
          ×
        </button>
        <span className="studio-eyebrow">LET’S TAKE A LOOK</span>
        <h2 id="consultation-title">{title}</h2>
        {saved ? (
          <div role="status">
            <h3>Your inquiry is saved.</h3>
            <p>
              {safeCalendar
                ? `Next, choose a time for your ${audit ? "UX audit consultation" : "project call"}. Your appointment is only confirmed after you complete the calendar booking.`
                : "We’ll email you to arrange a time. No appointment has been booked yet."}
            </p>
            {safeCalendar && (
              <a
                className="studio-button gold"
                href={safeCalendar}
                target="_blank"
                rel="noopener noreferrer"
              >
                {audit
                  ? "Choose an audit consultation time"
                  : "Choose a project call time"}{" "}
                ↗
              </a>
            )}
            <p className="studio-form-note">
              {audit
                ? "The consultation is free. UX/UI audits start at $1,000, depending on complexity. We’ll agree on scope and price before starting."
                : "We’ll discuss your goals, the approach, and next steps."}
            </p>
            <button className="studio-text-button" onClick={onClose}>
              Done
            </button>
          </div>
        ) : (
          <>
            <p>
              {audit
                ? "For teams ready to invest in their product’s UX. The consultation is free; audits typically start at $1,000, depending on the app’s scope and the work required. We’ll agree on scope and price before you commit."
                : "A free 15-minute introduction to your idea or existing application. Tell us where you could use a hand."}
            </p>
            <form onSubmit={submit}>
              {interests.length > 0 && <div className="selected-interests"><strong>You selected</strong><ul>{interests.map(interest => <li key={interest}><span aria-hidden="true">✓ </span>{interest}</li>)}</ul></div>}
              <div className="studio-form-row">
                <label>
                  First name
                  <input
                    name="first_name"
                    autoComplete="given-name"
                    required
                    maxLength={100}
                  />
                </label>
                <label>
                  Last name
                  <input
                    name="last_name"
                    autoComplete="family-name"
                    required
                    maxLength={100}
                  />
                </label>
              </div>
              <label>
                Email
                <input
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  maxLength={254}
                />
              </label>
              <label>
                Phone <span>(optional)</span>
                <input
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  maxLength={30}
                />
              </label>
              <label>
                Your role
                <select name="role_type" required defaultValue="">
                  <option value="" disabled>
                    Select your role
                  </option>
                  <option value="founder">Founder / business owner</option>
                  <option value="employee">Team member</option>
                  <option value="business_representative">
                    Company representative
                  </option>
                  <option value="other">Other</option>
                </select>
              </label>
              <label>
                {audit
                  ? "Product or website URL (optional)"
                  : "Where are you starting?"}
                {audit ? (
                  <input name="context" type="url" placeholder="https://" />
                ) : (
                  <select name="context" defaultValue={ai ? "AI training or business integration" : "An idea for a new product"}>
                    <option>An idea for a new product</option>
                    <option>AI training or business integration</option>
                    <option>Security review or engineering support</option>
                    <option>An MVP in progress</option>
                    <option>An existing application</option>
                    <option>Planning / tools advice</option>
                  </select>
                )}
              </label>
              <label>
                {audit
                  ? "What would you like to improve?"
                  : "What would you like to build or improve?"}
                <textarea
                  name="description"
                  required
                  rows={3}
                  maxLength={6000}
                  placeholder={
                    audit
                      ? "For example: onboarding, checkout, or a flow users find confusing."
                      : "A little about your goals and where you need support."
                  }
                />
              </label>
              <div className="studio-form-row">
                <label>
                  Timeline <span>(optional)</span>
                  <input
                    name="timeline"
                    maxLength={200}
                    placeholder="e.g. Over the next 3 months"
                  />
                </label>
                <label>
                  Budget in USD <span>(optional)</span>
                  <input
                    name="budget"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="If known"
                  />
                </label>
              </div>
              <p className="studio-form-note">
                We’ll use these details to respond to your inquiry.{" "}
                <a
                  href="/privacy.html"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy policy
                </a>
                . No SMS signup or AI summarization.
              </p>
              {error && (
                <p role="alert" className="studio-form-error">
                  {error}
                </p>
              )}
              <button
                className="studio-button gold"
                type="submit"
                disabled={busy}
              >
                {busy ? "Saving…" : "Save inquiry & continue"}
              </button>
              <p className="studio-form-note">
                {safeCalendar
                  ? "Next: choose a time in our calendar."
                  : "We’ll follow up by email to arrange a time."}
              </p>
            </form>
          </>
        )}
      </section>
    </div>
  );
}
